import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { analyzeRetina, normalizeAnalysis, ensureToken } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import EnhancedAnalysisResult from '../components/EnhancedAnalysisResult';
import AssistantPanel from '../components/AssistantPanel';
import DemographicsFields from '../components/DemographicsFields';
import ClinicalContextFields from '../components/ClinicalContextFields';

const RETINA_CONDITIONS = [
  { name: 'Normal', desc: 'No detectable disease findings', color: 'bg-emerald-900/40 border-emerald-700 text-emerald-300' },
  { name: 'Diabetic Retinopathy', desc: 'Diabetes-related retinal damage', color: 'bg-orange-900/40 border-orange-700 text-orange-300' },
  { name: 'Glaucoma', desc: 'Optic nerve damage — high risk of vision loss if untreated', color: 'bg-red-900/40 border-red-700 text-red-300' },
  { name: 'Age-Related Macular Degeneration', desc: 'Central vision deterioration — high risk of vision loss if untreated', color: 'bg-red-900/40 border-red-700 text-red-300' },
  { name: 'Hypertensive Retinopathy', desc: 'Blood-pressure-related retinal vessel damage', color: 'bg-orange-900/40 border-orange-700 text-orange-300' },
  { name: 'Cataract', desc: 'Lens clouding — treatable, non-urgent', color: 'bg-blue-900/40 border-blue-700 text-blue-300' },
  { name: 'Myopia (Refractive)', desc: 'Nearsightedness — structural, non-urgent', color: 'bg-blue-900/40 border-blue-700 text-blue-300' },
  { name: 'Other Abnormality', desc: 'Flagged for clinician review', color: 'bg-gray-800/40 border-gray-600 text-gray-300' },
];

const RetinaAnalysis = () => {
  const { currentUser } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeCondition, setActiveCondition] = useState(null);
  const [demographics, setDemographics] = useState({});
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setError(null);
    if (!selectedFile) { setFile(null); setPreview(null); return; }
    if (!selectedFile.type.startsWith('image/')) {
      setError('Please select an image file'); setFile(null); setPreview(null); return;
    }
    setFile(selectedFile);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.dataTransfer.files?.[0]) handleFileChange({ target: { files: e.dataTransfer.files } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please select a fundus image to analyse'); return; }
    setIsAnalyzing(true); setResult(null); setError(null);
    try {
      const token = await ensureToken(currentUser?.token);
      const resp = await analyzeRetina(file, token, demographics);
      if (resp.model_status !== 'ready') {
        setError('The retina AI model is warming up — please try again in a moment.'); return;
      }
      const mapped = normalizeAnalysis(resp);
      setResult(mapped);
      setActiveCondition(mapped.conditions[0]?.name || null);
    } catch (err) {
      setError(err.message || 'An error occurred during analysis');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleReset = () => {
    setFile(null); setPreview(null); setResult(null);
    setError(null); setActiveCondition(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Navbar />
      <main className="flex-grow py-10 page-transition">
        <div className="container-custom">
          {/* Header */}
          <div className="flex items-center mb-8">
            <Link to="/analysis" className="text-emerald-400 hover:text-emerald-300 mr-4 flex items-center">
              <svg className="h-5 w-5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="ml-2">Back to Analyses</span>
            </Link>
            <div>
              <h1 className="heading-secondary">Retinal Fundus Analysis</h1>
              <p className="text-emerald-400 text-sm mt-0.5">Multi-Disease Retina Screening · ODIR-5K Fine-Tuned Model</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Upload panel */}
            <div className="bg-gray-900 rounded-xl shadow-md p-6 border border-gray-800">
              <h2 className="heading-subsection mb-4">Upload Fundus Image</h2>

              {error && (
                <div className="bg-red-900/30 border-l-4 border-red-500 p-4 mb-4 text-red-400">
                  <p>{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div
                  className="border-2 border-dashed border-gray-700 hover:border-emerald-500 transition-colors duration-300 rounded-lg p-8 text-center cursor-pointer"
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {preview ? (
                    <img src={preview} alt="Fundus preview" className="mx-auto max-h-64 object-contain rounded" />
                  ) : (
                    <div className="mb-4">
                      <div className="mx-auto h-24 w-24 flex items-center justify-center bg-gray-800 rounded-full mb-4">
                        <svg className="h-12 w-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </div>
                      <p className="text-gray-400 mt-2">Drag and drop a fundus photo, or click to select</p>
                      <p className="text-gray-500 text-sm mt-1">JPEG / PNG · Non-mydriatic or mydriatic fundus camera</p>
                    </div>
                  )}
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                </div>

                <div className="bg-emerald-900/20 border-l-4 border-emerald-600 p-4">
                  <p className="text-sm text-emerald-300">
                    <strong>For best results:</strong> Upload a clear colour fundus photograph centred on the optic disc or macula.
                    Both mydriatic and non-mydriatic images are supported.
                  </p>
                </div>

                <DemographicsFields value={demographics} onChange={setDemographics} showDiabetesFields />
                <div className="mt-4">
                  <ClinicalContextFields value={demographics} onChange={setDemographics} />
                </div>

                <div className="flex justify-end space-x-3">
                  <button type="button" onClick={handleReset}
                    className="px-4 py-2 border border-gray-700 rounded-md text-sm font-medium text-gray-300 hover:bg-gray-800 transition-colors">
                    Reset
                  </button>
                  <button type="submit" disabled={!file || isAnalyzing}
                    className={`px-4 py-2 rounded-md text-sm font-medium text-white transition-colors ${
                      !file || isAnalyzing
                        ? 'bg-emerald-900/40 cursor-not-allowed'
                        : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700'
                    }`}>
                    {isAnalyzing ? 'Analysing...' : 'Analyse Fundus Image'}
                  </button>
                </div>
              </form>
            </div>

            {/* Results panel */}
            <div className="bg-gray-900 rounded-xl shadow-md p-6 border border-gray-800">
              <h2 className="heading-subsection mb-4">Retina Screening Result</h2>

              {isAnalyzing ? (
                <div className="flex flex-col items-center justify-center h-64">
                  <div className="relative h-20 w-20">
                    <div className="absolute inset-0 rounded-full border-t-2 border-b-2 border-emerald-500 animate-spin" />
                    <div className="absolute inset-2 rounded-full border-r-2 border-l-2 border-teal-500 animate-spin animation-delay-150" />
                    <div className="absolute inset-4 rounded-full border-t-2 border-b-2 border-green-400 animate-spin animation-delay-300" />
                  </div>
                  <p className="mt-6 text-gray-300">Screening for retinal disease...</p>
                  <p className="mt-2 text-gray-500 text-sm">Usually takes 5–15 seconds</p>
                </div>
              ) : result ? (
                <div className="space-y-6">
                  <EnhancedAnalysisResult
                    image={preview}
                    conditions={result.conditions}
                    activeCondition={activeCondition}
                    onConditionClick={(c) => setActiveCondition(c.name)}
                    findings={result.findings}
                    confidence={result.confidence}
                    recommendation={result.recommendation}
                  />
                  <AssistantPanel
                    analysisId={result.id}
                    assistant={result.assistant}
                    assistantName={result.assistantName}
                    disclaimer={result.disclaimer}
                    severity={result.conditions[0]?.severity}
                    risk={result.conditions[0]?.severity === 'Severe' ? 'malignant' : null}
                    chatContext={{
                      modality: 'retina',
                      top_finding: result.conditions[0]?.name,
                      severity: result.conditions[0]?.severity,
                      age: demographics.age,
                      sex: demographics.sex,
                    }}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <div className="bg-gray-800 h-24 w-24 rounded-full flex items-center justify-center mb-4">
                    <svg className="h-12 w-12 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                  <p className="text-gray-400">Upload a fundus photo to screen for retinal disease</p>
                </div>
              )}
            </div>
          </div>

          {/* Retina condition reference */}
          <div className="mt-8 bg-gray-900 rounded-xl p-6 border border-gray-800">
            <h2 className="heading-subsection mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Conditions Screened
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              {RETINA_CONDITIONS.map((c) => (
                <div key={c.name} className={`rounded-lg border p-3 ${c.color}`}>
                  <div className="text-sm font-bold mb-1">{c.name}</div>
                  <div className="text-xs opacity-80">{c.desc}</div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-gray-400 text-sm">
              Model: multi-disease ViT fine-tuned on the ODIR-5K ocular disease dataset, extending beyond
              diabetic-retinopathy-only screening to also flag glaucoma and age-related macular degeneration.
              Annual fundus screening is recommended for all patients regardless of result, especially those
              with diabetes or hypertension.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RetinaAnalysis;
